import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { EnvironmentService } from '@core/services/environment/environment.service';
import { TRACKING_PERSISTENCE } from '@core/state/persistence';
import { initialTrackingState, TrackingState } from './tracking.types';

const SESSION_ID_KEY = 'd2d_session_id';

export const TrackingStore = signalStore(
  { providedIn: 'root' },
  withState<TrackingState>(initialTrackingState),
  withComputed(({ sessionId }) => ({
    hasSessionId: computed(() => sessionId() !== null),
  })),
  withMethods((store) => {
    const persistence = inject(TRACKING_PERSISTENCE);
    const env = inject(EnvironmentService);

    return {
      restoreFromStorage(): void {
        const persisted = persistence.get<string>(SESSION_ID_KEY);
        if (typeof persisted === 'string' && persisted.length > 0) {
          patchState(store, { sessionId: persisted });
        }
      },

      /**
       * Returns the current session id, generating + persisting one on first use.
       * SSR-safe: returns null on the server (no UUID, no storage).
       */
      ensureSessionId(): string | null {
        if (env.isServer()) return null;
        const existing = store.sessionId();
        if (existing) return existing;
        const id = crypto.randomUUID();
        patchState(store, { sessionId: id });
        persistence.set(SESSION_ID_KEY, id);
        return id;
      },

      /** Forget the current session id (e.g. on logout). */
      reset(): void {
        patchState(store, { sessionId: null });
        persistence.remove(SESSION_ID_KEY);
      },
    };
  }),
  withHooks({
    onInit(store) {
      store.restoreFromStorage();
    },
  }),
);
